import { clinicAuthApi, clinicApi } from "@/domains/multi/clinicApi";
import { formatWorkScheduleSlots } from "@/domains/multi/workSchedule";
import { authMeQueryKey, useAuth } from "@/domains/shared/hooks/useAuth";
import { Button } from "@/domains/shared/components/Button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/domains/shared/components/Dialog";
import { describeApiError } from "@/domains/shared/services/api";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getTranslation } from "@mivo/i18n";
import { CLINIC_WORK_SCHEDULE_TRANSLATIONS as T, CLINIC_WORK_SCHEDULE_UI_TRANSLATIONS as U } from "../clinicDialogConstants";

export default function ClinicWorkScheduleApproveDialog() {
    const { user, refreshMe } = useAuth();
    const queryClient = useQueryClient();
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [slotLabels, setSlotLabels] = useState<string[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    const show = Boolean(user?.pendingWorkScheduleProposal && user?.activeClinicId) && !dismissed;
    const membershipId = user?.activeClinicMembershipId ?? null;

    useEffect(() => {
        setDismissed(false);
    }, [user?.id, user?.pendingWorkScheduleProposal, user?.activeClinicId]);

    useEffect(() => {
        if (!show || !membershipId) {
            setSlotLabels([]);
            return;
        }
        let cancelled = false;
        setLoadingSlots(true);
        void clinicApi
            .getWorkSchedule(membershipId)
            .then(({ data }) => {
                if (!cancelled) {
                    setSlotLabels(formatWorkScheduleSlots(data.slots ?? []));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingSlots(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [show, membershipId]);

    const approve = async () => {
        setBusy(true);
        try {
            await clinicAuthApi.approveWorkSchedule();
            toast.success(getTranslation(T.approved));
            await refreshMe();
            await queryClient.invalidateQueries({ queryKey: authMeQueryKey });
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { detail?: unknown; code?: string } } };
            toast.error(describeApiError(ax) || getTranslation(T.approveFailed));
        } finally {
            setBusy(false);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Dialog open={show} onOpenChange={() => undefined}>
            <DialogContent
                dismissable={false}
                scrollBody
                className="max-w-lg rounded-3xl"
            >
                <DialogHeader>
                    <DialogTitle>
                        {getTranslation(U.title)}
                    </DialogTitle>
                </DialogHeader>
                <DialogBody className="space-y-4">
                    <p className="text-sm leading-relaxed text-foreground">
                        {getTranslation(T.approveProposalBody)}
                    </p>
                    {loadingSlots ? (
                        <p className="text-sm text-muted-foreground/80">{getTranslation(T.loadingProposed)}</p>
                    ) : slotLabels.length > 0 ? (
                        <ul className="space-y-1 rounded-xl border border-border bg-muted/40 p-4 text-sm text-foreground">
                            {slotLabels.map((label) => (
                                <li key={label}>{label}</li>
                            ))}
                        </ul>
                    ) : null}
                </DialogBody>
                <DialogFooter className="mx-0 mb-0 gap-2 sm:gap-3">
                    <Button className="w-full sm:w-auto" isLoading={busy} onClick={() => void approve()}>
                        {getTranslation(T.approveButton)}
                    </Button>
                    <Button variant="outline" className="w-full sm:w-auto" onClick={() => setDismissed(true)}>
                        {getTranslation(T.decideLater)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
